// The one Firmware card on an access point's page (#1841). Gabriel Provencher, 2026-10-17: *"we
// should have only a single firmware upgrade/delivery card. i can still see two. they need to be
// merged together and be kept as minimalist as possible"* and *"I need a way to close the card."*
// Recorded as dated notes on ADR-0413 `6a`, `8a` and `10a`, ADR-0410 decision 6 and ADR-0403 `1a`.
//
// **One card, three states, one place.** Closed, nothing is drawn. Opened by Actions ▾ › Upgrade firmware… or the Firmware fact (#1889), or
// by *Next › Upgrade anyway*, it is the act (`FirmwareAct`): the consequence line, the builds and
// one button. After the click, and whenever the device has a delivery to show, it is the result:
// *From → To*, the state word, *Started at*, the bar and *Next* (`DeliveryStory`). The answer's own
// delivery is shown at once and the page's re-read replaces it. On this page no notice is drawn for
// a firmware answer: the card is the answer.
//
// **While a delivery is under way the card is the result and never the act** (#1844, Gabriel
// Provencher on W001-BASEMENT, 2026-10-17: *"the progress disappeared !"*; a dated note on ADR-0413
// `6a`). Under way is `markerOf`'s `under_way`: exactly the states a second click is refused in
// (ADR-0403 `3a`). Upgrade pressed then shows the progress, reopening a card the viewer closed, and
// leaves the act; once the delivery is no longer under way, Upgrade opens the act as before.
//
// **The force is one click, on the refused delivery's own card** (#1845, Gabriel Provencher,
// 2026-10-17: *"Why do I have to click twice on upgrade anyway, on two different screens ?"*; dated
// notes on ADR-0413 `5d` and `7.3a` and ADR-0411 `9b`). Where the newest delivery's next step is
// `force_available`, the result view's *Upgrade anyway* sends the forced request itself, under the
// one failure line, and the act no longer offers it. The request is the one the act's force sent:
// the entry the refusal answered, `overrides_delivery` naming the refusal, `accept_unknown_source` the
// line the options read served (`running_line`), and `accept_line_change` the entry's predicted build
// where the entry asks it. Refuse first, then a second request citing the refusal: unchanged.
//
// **The × closes it in every state, and closing changes nothing it sent.** A delivery under way is
// still followed (the page re-reads on `/v1/changes` whether the card is drawn or not), a request in
// flight still completes, and Upgrade brings the card back. A closed card stays closed in this
// browser until its delivery moves or a newer delivery replaces it (`closedMarkOf`; Gabriel
// Provencher, 2026-10-17: *"per viewer in the browser"*). Where the browser keeps nothing it is
// shown. The six hours a *Delivered* card stays (ADR-0410 decision 6) are unchanged.
import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { SKIP, useQuery } from "../../api/hooks";
import type { useMutation } from "../../api/mutation";
import type { components } from "../../api/schema";
import { RECORDED_NOT_SENT, sendFirmwareRequest, type Device, type FirmwareAnswer, type FirmwareDelivery, type FirmwareRequestAcceptance } from "./commands";
import { closedMarkOf, DeliverySection, FirmwareFrame, markerOf, readClosed, writeClosed } from "./delivery";
import { FirmwareAct } from "./FirmwareAct";

type Options = components["schemas"]["DeviceFirmwareRequestOptions"];

/** The newer of the page's newest delivery and the one the click answered with: the re-read once
 * it has caught up, the answer until then. */
function newerOf(read?: FirmwareDelivery, answered?: FirmwareDelivery): FirmwareDelivery | undefined {
  if (!answered) return read;
  if (!read) return answered;
  if (read.id === answered.id) return read;
  return Date.parse(answered.created_at) > Date.parse(read.created_at) ? answered : read;
}

export default function FirmwareCard({ device, newest, m, acting, mayAct, onCloseAct, onAnswered, now }: {
  device: Device;
  /** The device's newest delivery, from the page's own read. */
  newest?: FirmwareDelivery;
  /** The page's one write lane (#189). */
  m: ReturnType<typeof useMutation>;
  /** The page opened the act (Actions ▾ › Upgrade firmware…, or the Firmware fact). */
  acting: boolean;
  /** The person may command this device: the force is then a button, else its step is text. */
  mayAct: boolean;
  /** Leaves the act: the page clears its panel and the lane's Problem. */
  onCloseAct(): void;
  /** The server answered the click; the page leaves the act and re-reads. */
  onAnswered(): void;
  /** The browser's clock, for a test to set. */
  now?: number;
}) {
  const [answer, setAnswer] = useState<FirmwareAnswer>();
  const [closed, setClosed] = useState(() => readClosed(device.serial));
  const d = newerOf(newest, answer?.delivery);
  const recorded = !!answer && !answer.delivery;
  // Restates ADR-0413 decision 6 (`6a`) as amended 2026-10-17 (#1844), amending how ADR-0371 decision
  // d.1a is drawn — while the newest delivery is under way the card shows its progress and not the act;
  // pressing Upgrade then brings that progress back and opens no act.
  const underWay = !!d && markerOf(d.state) === "under_way";
  const upgradeDuringDelivery = acting && underWay;
  // A card the viewer closed comes back when Upgrade is pressed during the delivery: adjusted while
  // rendering, as React adjusts state from props, and the page's open act is left after it.
  if (upgradeDuringDelivery && closed !== undefined) setClosed(undefined);
  useEffect(() => {
    if (upgradeDuringDelivery) onCloseAct();
  }, [upgradeDuringDelivery, onCloseAct]);
  // The force's entry, read only where the newest delivery offers it (#1845): the server's own options
  // for this device, whose entry names the refusal and carries the line the device reports now.
  const forceOffered = mayAct && !!d && d.state === "refused" && d.next_step === "force_available";
  const options = useQuery<Options>(() => forceOffered
    ? api.GET("/devices/{serial}/firmware-request-options", { params: { path: { serial: device.serial } } })
    : SKIP, [device.serial, forceOffered, d?.id]);
  const forceEntry = forceOffered && options.status === "ready"
    ? options.data.entries.find((e) => e.next_step === "force_available" && e.refused_delivery === d?.id) : undefined;
  const sendForce = async () => {
    if (!forceEntry?.refused_delivery || !forceEntry.running_line || !forceEntry.predicted_firmware) return;
    const accept: FirmwareRequestAcceptance = { overrides: forceEntry.refused_delivery, unknownSource: forceEntry.running_line };
    if (forceEntry.acceptances.includes("firmware_line_change")) accept.line = forceEntry.predicted_firmware;
    const a = await sendFirmwareRequest(m, device, forceEntry.artifact, accept);
    if (a) { setAnswer(a); onAnswered(); }
  };
  const force = forceOffered ? {
    name: device.name, onClick: () => void sendForce(),
    disabled: m.busy || !forceEntry?.running_line,
    title: forceEntry?.running_line ? undefined : options.status === "loading" ? "Reading the firmware line this device runs." : "The firmware line this device runs could not be read for this upgrade.",
  } : undefined;
  const close = () => {
    if (d) { const mark = closedMarkOf(d); writeClosed(device.serial, mark); setClosed(mark); }
    if (recorded) setAnswer(undefined);
    if (acting) onCloseAct();
  };
  if (acting && !underWay) {
    return (
      <FirmwareFrame onClose={close}>
        <FirmwareAct device={device} m={m} onClose={close} onAnswer={(a) => { setAnswer(a); onAnswered(); }} />
      </FirmwareFrame>
    );
  }
  if (recorded) return <FirmwareFrame onClose={close}><p className="muted">{RECORDED_NOT_SENT}</p></FirmwareFrame>;
  if (!d || (!upgradeDuringDelivery && closed === closedMarkOf(d))) return null;
  return <DeliverySection key={`${d.id}:${d.updated_at}`} device={device} newest={d} now={now} force={force} onClose={close} />;
}
